import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { contentApi } from '../../../api/swagger'
import { useAppSnackbar } from '../../../hooks/useAppSnackbar'
import useLoadingContext from '../../../hooks/useLoadingContext'
import { FetchLearningCenterOptionsProgramTypeEnum } from '../../../swagger'
import {
  FilterFileByChallengeLevel,
  FilterLanguage,
  LearningCenterTabs,
} from '../../../utils/searchAndFilterEnums'
import {
  defaultLearningCenterContextValue,
  useLearningCenterContext,
} from '../../Context/LearningCenterContext'
import { useAuth } from '../../Routes/AuthProvider'

export const stringToChallengeLevelMap = new Map<
  string,
  FilterFileByChallengeLevel
>([
  ['Challenge A', FilterFileByChallengeLevel.ChallengeA],
  ['Challenge B', FilterFileByChallengeLevel.ChallengeB],
  ['Challenge I', FilterFileByChallengeLevel.Challenge1],
  ['Challenge II', FilterFileByChallengeLevel.Challenge2],
  ['Challenge III', FilterFileByChallengeLevel.Challenge3],
  ['Challenge IV', FilterFileByChallengeLevel.Challenge4],
])

export const useLearningCenterOptions = (options: {
  /** If not provided, don't call the endpoint. */
  programType?: FetchLearningCenterOptionsProgramTypeEnum
}): { isLoading: boolean } => {
  const { programType } = options
  const { t } = useTranslation()
  const { featureAbility } = useAuth()
  const { handleError } = useAppSnackbar()

  const {
    updateLearningCenterOptions,
    optionsLoadingId: loadingId,
    level,
    selectedTabKey,
    welcomeCenterContent,
    updateCycle,
    tabSpecificSearchAndFilter,
    updateTabSpecificSearchAndFilter,
  } = useLearningCenterContext()
  const isChallengeTab = selectedTabKey.includes(LearningCenterTabs.Challenge)
  const hasLearningCenterAccess = featureAbility.can(
    'learningCenter',
    'Feature'
  )

  /**
   * If the tab's selected language isn't available in the newly loaded options,
   * reset it. Otherwise the dropdown could be hidden while a stale language
   * keeps the page empty, e.g. when changing Challenge levels.
   */
  const resetUnavailableLanguage = (
    languages: string[],
    cycle?: number
  ): void => {
    const tabFilters = tabSpecificSearchAndFilter[selectedTabKey]
    if (
      !tabFilters ||
      tabFilters.language === FilterLanguage.AllLanguages ||
      languages.includes(tabFilters.language)
    )
      return

    updateTabSpecificSearchAndFilter({
      ...tabSpecificSearchAndFilter,
      [selectedTabKey]: {
        ...tabFilters,
        cycle,
        language: FilterLanguage.AllLanguages,
      },
    })
  }

  const fetchWelcomeCenterOptions = async () => {
    const { languages } = await contentApi.fetchLearningCenterOptions({
      welcomeCenterContent: true,
    })

    // The Welcome Center only filters by language, and doesn't use a cycle.
    updateLearningCenterOptions({
      ...defaultLearningCenterContextValue.learningCenterOptions,
      languages,
    })
    resetUnavailableLanguage(languages)
  }

  const fetchLearningCenterOptions = async () => {
    if (welcomeCenterContent) {
      try {
        await fetchWelcomeCenterOptions()
      } catch (e) {
        await handleError(
          e,
          t(
            'Endpoints.FetchLearningCenterOptions.Error',
            'An unknown error occurred fetching learning center options.'
          )
        )
      }
      return
    }

    if (
      (level === undefined && isChallengeTab) ||
      !programType ||
      selectedTabKey === LearningCenterTabs.Playlist
    )
      return

    const programTypeValue = (
      isChallengeTab ? level : programType
    ) as FetchLearningCenterOptionsProgramTypeEnum

    try {
      const { categories, filetypes, cycles, languages } =
        await contentApi.fetchLearningCenterOptions({
          programType: programTypeValue,
        })

      /**
       * Update the options within the LearningCenterContext. No need to have state
       * variables within this hook since our context will hold the information
       * for child components.
       */
      updateLearningCenterOptions({
        categories,
        filetypes,
        cycles,
        languages,
      })
      const defaultCycle = cycles?.find((cycle) => cycle.isDefault)?.cycle ?? 0
      updateCycle(defaultCycle)
      resetUnavailableLanguage(languages, defaultCycle)
    } catch (e) {
      await handleError(
        e,
        t(
          'Endpoints.FetchLearningCenterOptions.Error',
          'An unknown error occurred fetching learning center options.'
        )
      )
    }
  }
  const {
    triggerFetch: fetchLearningCenterTrigger,
    removeLoadingIds,
    isLoading,
  } = useLoadingContext({
    loadingId,
    asyncFunction: fetchLearningCenterOptions,
  })

  useEffect(() => {
    /** Don't call the endpoint if we can't access the feature */
    if (
      (welcomeCenterContent || !!programType || (!!level && isChallengeTab)) &&
      hasLearningCenterAccess
    ) {
      fetchLearningCenterTrigger()
    }

    removeLoadingIds([loadingId])
  }, [
    hasLearningCenterAccess,
    welcomeCenterContent,
    programType,
    level,
    isChallengeTab,
    fetchLearningCenterTrigger,
    removeLoadingIds,
    loadingId,
  ])

  return { isLoading }
}
